import { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { Button, Form } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import EditableDataTable from "@components/EditableDataTable/EditableDataTable";
import { Column } from "@components/EditableDataTable/types/column";
import DataTable from "@components/DataTable";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import SearchField from "@components/Forms/SearchField/SearchField";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { useV2UpdateBlueprintMutation } from "@generated";
import { useGetAccount } from "../../common/api";
import type { BlueprintDetail } from "../../common/api";
import "./style.css";

export interface BlueprintItemsTabHandle {
  getPayloadUpdates: (basePayload: any[]) => any[];
}

export interface TabRow {
  id: string;
  [key: string]: any;
}

interface CatalogPagination {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRows: number;
  setCurrentPage: (p: number) => void;
  setPageSize?: (s: number) => void;
}

interface Props {
  blueprint: BlueprintDetail;
  refetch: () => void;
  payloadType: string;
  toTabRow: (item: any, idx: number) => TabRow;
  toPayloadItem: (row: TabRow) => any;
  tabColumns: Column[];
  catalogColumns: ColumnDef<TabRow>[];
  catalogItems: TabRow[];
  catalogLoading: boolean;
  onCatalogSearch: (q: string) => void;
  catalogPagination?: CatalogPagination;
  itemLabel: string;
  editableQuantity?: boolean;
  readOnly?: boolean;
}

const BlueprintItemsTab = forwardRef<BlueprintItemsTabHandle, Props>(
  (
    {
      blueprint,
      refetch,
      payloadType,
      toTabRow,
      toPayloadItem,
      tabColumns,
      catalogColumns,
      catalogItems,
      catalogLoading,
      onCatalogSearch,
      catalogPagination,
      itemLabel,
      editableQuantity,
      readOnly,
    },
    ref
  ) => {
    const selectLabel = `Add ${itemLabel}`;
    const emptyMessage = `No ${itemLabel.toLowerCase()} added. ${
      readOnly ? "" : `Click '${selectLabel}' to get started.`
    }`;
    const { accountId } = useGetAccount();
    const [selectedDeleteIds, setSelectedDeleteIds] = useState<string[]>([]);
    const [selectedAddItems, setSelectedAddItems] = useState<TabRow[]>([]);
    const selectedAddIds = selectedAddItems.map((i) => i.id);

    const handleCatalogSelection = (newIds: string[]) => {
      setSelectedAddItems((prev) => {
        const kept = prev.filter((item) => newIds.includes(item.id));
        const addedIds = newIds.filter(
          (id) => !prev.some((item) => item.id === id)
        );
        const added = catalogItems.filter((item) => addedIds.includes(item.id));
        return [...kept, ...added];
      });
    };
    const [slideoutOpen, setSlideoutOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showFailAlert, setShowFailAlert] = useState(false);
    const localQuantitiesRef = useRef<Record<string, number>>({});

    const [updateBlueprint] = useV2UpdateBlueprintMutation();

    useImperativeHandle(
      ref,
      () => ({
        getPayloadUpdates: (basePayload: any[]) => {
          if (!editableQuantity) return basePayload;
          return basePayload.map((item) => {
            if (item.type !== payloadType) return item;
            const qty = localQuantitiesRef.current[String(item.id ?? item.lid)];
            return qty !== undefined
              ? { ...item, attributes: { ...item.attributes, quantity: qty } }
              : item;
          });
        },
      }),
      [editableQuantity, payloadType]
    );

    const displayColumns: Column[] = editableQuantity
      ? tabColumns.map((col) =>
          col.id === "quantity"
            ? {
                ...col,
                cell: (row: TabRow) => (
                  <div style={{ width: "80px", margin: "-5px 0px" }}>
                    <Form.Control
                      type="number"
                      defaultValue={row.quantity ?? 1}
                      disabled={readOnly}
                      onChange={(e) => {
                        localQuantitiesRef.current[row.id] = Number(
                          e.target.value
                        );
                      }}
                    />
                  </div>
                ),
              }
            : col
        )
      : tabColumns;

    const blueprintId = Number(blueprint.id);
    const currentPayload = blueprint.rawPayload;

    const typeItems = currentPayload.filter((p) => p.type === payloadType);
    const tabItems = typeItems.map((item, idx) => toTabRow(item, idx));
    const isEmpty = tabItems.length === 0;

    const patchPayload = async (newPayload: any[]) => {
      await updateBlueprint({
        id: String(blueprintId),
        body: {
          data: {
            id: String(blueprintId),
            type: "blueprints",
            attributes: {
              name: blueprint.name,
              payload: newPayload,
            },
            relationships: {
              account: { data: { type: "accounts", id: String(accountId) } },
            },
          },
        },
      }).unwrap();
      await refetch();
    };

    const handleAdd = async () => {
      const toAdd = selectedAddItems.map((c) => toPayloadItem(c));

      setSaving(true);
      try {
        await patchPayload([...currentPayload, ...toAdd]);
        setSelectedAddItems([]);
        setSlideoutOpen(false);
        setSearchInput("");
        onCatalogSearch("");
        setSuccessMessage(
          `${toAdd.length} item${toAdd.length !== 1 ? "s" : ""} added.`
        );
        setShowSuccessAlert(true);
      } catch (err: any) {
        setErrorMessage(
          err?.data?.errors?.[0]?.detail || "Failed to add items."
        );
        setShowFailAlert(true);
      } finally {
        setSaving(false);
      }
    };

    const handleDelete = async () => {
      const selectedSet = new Set(selectedDeleteIds);
      const newPayload = currentPayload.map((p) => {
        if (p.type !== payloadType) return p;
        const idx = typeItems.indexOf(p);
        if (!selectedSet.has(toTabRow(p, idx).id)) return p;
        return { ...p, attributes: { ...p.attributes, active: false } };
      });

      setSaving(true);
      try {
        await patchPayload(newPayload);
        const count = selectedDeleteIds.length;
        setSelectedDeleteIds([]);
        setShowDeleteModal(false);
        setSuccessMessage(`${count} item${count !== 1 ? "s" : ""} removed.`);
        setShowSuccessAlert(true);
      } catch (err: any) {
        setErrorMessage(
          err?.data?.errors?.[0]?.detail || "Failed to remove items."
        );
        setShowFailAlert(true);
      } finally {
        setSaving(false);
      }
    };

    const handleSearch = (e: React.FormEvent) => {
      e.preventDefault();
      onCatalogSearch(searchInput);
    };

    const closeSlideout = () => {
      setSlideoutOpen(false);
      setSelectedAddItems([]);
      setSearchInput("");
      onCatalogSearch("");
    };

    return (
      <div className="blueprintItemsTab">
        {handleAlert(
          showSuccessAlert,
          successMessage,
          setShowSuccessAlert,
          "success",
          ToastAlert
        )}
        {handleAlert(
          showFailAlert,
          errorMessage,
          setShowFailAlert,
          "warning",
          ToastAlert
        )}

        <div
          className={`tabItemsHeader${
            selectedDeleteIds.length || isEmpty ? " hasSelection" : ""
          }`}
        >
          {!readOnly && selectedDeleteIds.length > 0 ? (
            <div className="selectedTabItems">
              <span className="selectedMessage">
                {selectedDeleteIds.length} Selected
              </span>
              <FontAwesomeIcon
                icon={faTrashCan}
                className="trashIcon"
                onClick={() => setShowDeleteModal(true)}
              />
            </div>
          ) : isEmpty ? (
            <div className="tabEmpty">{emptyMessage}</div>
          ) : null}
          {!readOnly && (
            <div className="actionBtns">
              <Button className="button" onClick={() => setSlideoutOpen(true)}>
                {selectLabel}
              </Button>
            </div>
          )}
        </div>

        {saving ? (
          <SmallSpinner />
        ) : !isEmpty ? (
          <EditableDataTable
            records={tabItems}
            columns={displayColumns}
            config={{ striped: true, hover: true, multiselect: !readOnly }}
            selectedIds={selectedDeleteIds}
            setSelectedIds={setSelectedDeleteIds}
          />
        ) : null}

        <ScopeStackModal
          modalTitle="Remove Items"
          modalBody={
            <p>
              Remove {selectedDeleteIds.length} item
              {selectedDeleteIds.length !== 1 ? "s" : ""} from this blueprint?
            </p>
          }
          button1Text="Cancel"
          button2Text="Remove"
          handleButton1Click={() => {
            setSelectedDeleteIds([]);
            setShowDeleteModal(false);
          }}
          handleButton2Click={handleDelete}
          show={showDeleteModal}
          handleClose={() => {
            setSelectedDeleteIds([]);
            setShowDeleteModal(false);
          }}
          deleteModal
        />

        <SlideOutMenuV2
          title={selectLabel}
          isOpen={slideoutOpen}
          onClose={closeSlideout}
          onSave={handleAdd}
          actionBtnText={`Add${
            selectedAddIds.length ? ` ${selectedAddIds.length}` : ""
          } Selected`}
          actionBtnDisabled={!selectedAddIds.length || saving}
        >
          {saving ? (
            <SmallSpinner />
          ) : (
            <>
              <FormFieldLabel
                label={`Search ${itemLabel}`}
                className={undefined}
              />
              <Form
                onSubmit={handleSearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch(e as any);
                }}
              >
                <SearchField
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onClick={() => {
                    setSearchInput("");
                    onCatalogSearch("");
                  }}
                  fullWidth
                />
              </Form>
              {catalogLoading ? (
                <SmallSpinner />
              ) : (
                <DataTable
                  data={
                    catalogItems.length
                      ? catalogItems
                      : [{ id: "empty", name: "No items found." }]
                  }
                  columns={catalogColumns}
                  selectable={!!catalogItems.length}
                  striped
                  hover
                  selectedIds={selectedAddIds}
                  setSelectedIds={handleCatalogSelection}
                  paginationEnabled={!!catalogPagination}
                  pageSizeEnabled={!!catalogPagination}
                  currentPage={catalogPagination?.currentPage ?? 1}
                  setCurrentPage={
                    catalogPagination?.setCurrentPage ?? (() => {})
                  }
                  pageSize={catalogPagination?.pageSize ?? 10}
                  onPageSizeChange={catalogPagination?.setPageSize}
                  totalPages={catalogPagination?.totalPages ?? 1}
                  totalRows={catalogPagination?.totalRows ?? 0}
                />
              )}
            </>
          )}
        </SlideOutMenuV2>
      </div>
    );
  }
);

BlueprintItemsTab.displayName = "BlueprintItemsTab";
export default BlueprintItemsTab;
